import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Play, Square, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useValidation } from '@/hooks/useValidation';
import { useGlobalAssistant } from '@/contexts/GlobalAssistantContext';
import { contentApi } from '@/services/contentApi';
import { playgroundApi } from '@/services/playgroundApi';
import { PlaygroundEditor } from '@/components/playground/PlaygroundEditor';
import { PlaygroundInput, PromptConfig } from '@/components/playground/PlaygroundInput';
import { PlaygroundOutput } from '@/components/playground/PlaygroundOutput';
import { PlaygroundChat } from '@/components/playground/PlaygroundChat';
import { PlaygroundObserver } from '@/components/playground/PlaygroundObserver';
import { AgentSelector, NEW_AGENT_VALUE } from '@/components/playground/AgentSelector';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: any[];
  isStreaming?: boolean;
}

export function PlaygroundPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { registerPageContext } = useGlobalAssistant();

  // Agent selection
  const [agents, setAgents] = useState<any[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState(searchParams.get('agent') || '');
  const [agentContent, setAgentContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [isNewAgent, setIsNewAgent] = useState(false);

  // All available content items for assistant
  const [allContentItems, setAllContentItems] = useState<any[]>([]);

  // Validation - uses useValidation hook with Pyodide
  const { errors: validationErrors, isValidating } = useValidation(
    agentContent,
    'agent',
    !!agentContent // enabled when there's content
  );

  // Auto-save state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [hasDraftChanges, setHasDraftChanges] = useState(false);

  // Execution mode
  const [mode, setMode] = useState<'oneshot' | 'interactive'>('oneshot');

  // Execution state
  const [isRunning, setIsRunning] = useState(false);
  const [trajectoryId, setTrajectoryId] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'running' | 'completed' | 'failed'>('idle');
  const [runError, setRunError] = useState<string | null>(null);

  // Output
  const [output, setOutput] = useState<string | null>(null);
  const [userPrompt, setUserPrompt] = useState('');
  const [inputVariables, setInputVariables] = useState<Record<string, string>>({});
  const [promptConfig, setPromptConfig] = useState<PromptConfig>({ mode: 'append' });

  // Chat messages (interactive mode)
  const [messages, setMessages] = useState<Message[]>([]);

  // Observer
  const [logs, setLogs] = useState('');
  const [traceEvents, setTraceEvents] = useState<any[]>([]);
  const [observerTab, setObserverTab] = useState<'logs' | 'trace'>('logs');

  // Panel tabs
  const [leftPanelTab, setLeftPanelTab] = useState<'code' | 'logs' | 'trace'>('code');

  // Refs
  const pollStopRef = useRef<(() => void) | null>(null);
  const chatControllerRef = useRef<AbortController | null>(null);

  // Load agents list and all content items
  useEffect(() => {
    async function loadAgents() {
      try {
        const items = await contentApi.listItems({ contentType: 'agent' });
        setAgents(items);
      } catch (error) {
        console.error('Failed to load agents:', error);
        toast({
          title: 'Error',
          description: 'Failed to load agents',
          variant: 'destructive',
        });
      }
    }
    loadAgents();

    // Load all content items for assistant context
    async function loadAllItems() {
      try {
        const types = ['agent', 'skill', 'prompt', 'judge'];
        const results = await Promise.all(
          types.map(type => contentApi.listItems({ contentType: type, limit: 500 }))
        );
        setAllContentItems(results.flat());
      } catch (error) {
        console.error('Failed to load all content items:', error);
      }
    }
    loadAllItems();
  }, [toast]);

  // Load selected agent content (skip for new agents - template is set in handleAgentSelect)
  useEffect(() => {
    async function loadAgentContent() {
      // Skip if no agent selected or if it's a new agent
      if (!selectedAgentId || selectedAgentId === NEW_AGENT_VALUE) {
        return;
      }

      try {
        const content = await contentApi.getContent(selectedAgentId);
        setAgentContent(content);
        setOriginalContent(content);
        setHasDraftChanges(false);
      } catch (error) {
        console.error('Failed to load agent content:', error);
        toast({
          title: 'Error',
          description: 'Failed to load agent content',
          variant: 'destructive',
        });
      }
    }
    loadAgentContent();
  }, [selectedAgentId, toast]);

  // Update URL when agent changes (don't put __new__ in URL)
  useEffect(() => {
    if (selectedAgentId && selectedAgentId !== NEW_AGENT_VALUE) {
      setSearchParams({ agent: selectedAgentId });
    } else {
      setSearchParams({});
    }
  }, [selectedAgentId, setSearchParams]);

  // Auto-save with debounce (only for existing agents)
  useEffect(() => {
    // Skip auto-save for new agents (no ID yet) or unchanged content
    if (!selectedAgentId || selectedAgentId === NEW_AGENT_VALUE || agentContent === originalContent) {
      return;
    }

    setHasDraftChanges(true);

    const timeout = setTimeout(async () => {
      setIsSaving(true);
      try {
        await contentApi.saveDraft(selectedAgentId, agentContent, 'Auto-save from playground');
        setLastSavedAt(new Date());
      } catch (error) {
        console.error('Failed to auto-save:', error);
      } finally {
        setIsSaving(false);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [agentContent, selectedAgentId, originalContent]);

  // Polling for logs and trace during execution
  useEffect(() => {
    if (!trajectoryId || !isRunning) {
      if (pollStopRef.current) {
        pollStopRef.current();
        pollStopRef.current = null;
      }
      return;
    }

    pollStopRef.current = playgroundApi.poll(
      trajectoryId,
      (data) => {
        setLogs(data.logs);
        // Map API event fields to component expected format
        const mappedEvents = (data.events || []).map((e: any) => ({
          id: e._id || e.event_id || e.id,
          type: e.event_type || e.type,
          name: e.data?.tool_name || e.data?.agent_name || e.name,
          timestamp: e.timestamp,
          data: e.data,
        }));
        setTraceEvents(mappedEvents);

        // Set output from trajectory result or extract from observation events
        if (data.result) {
          setOutput(data.result);
        } else {
          // Try to extract from the last observation event
          const lastObservation = mappedEvents
            .filter((e: any) => e.type === 'observation_emitted')
            .pop();
          if (lastObservation?.data?.content) {
            setOutput(lastObservation.data.content);
          }
        }

        if (data.status !== 'running') {
          setIsRunning(false);
          // Status can be 'completed', 'failed', 'success', etc.
          setStatus(data.status === 'completed' || data.status === 'success' ? 'completed' : 'failed');
        }
      },
      2000,  // Poll every 2 seconds
      true   // Auto-stop on completion
    );

    return () => {
      if (pollStopRef.current) {
        pollStopRef.current();
        pollStopRef.current = null;
      }
    };
  }, [trajectoryId, isRunning]);

  // Register playground context for global assistant
  useEffect(() => {
    registerPageContext({
      contextType: 'playground',
      playground: {
        agent_content: agentContent,
        run_status: status,
        run_error: runError || undefined,
        logs: logs || undefined,
        last_output: output || undefined,
        available_files: allContentItems.map(item => ({
          id: item.id,
          name: item.name,
          content_type: item.content_type,
        })),
      },
    });
  }, [registerPageContext, agentContent, status, runError, logs, output, allContentItems]);

  // Default template for new agents
  const NEW_AGENT_TEMPLATE = `---
name: my-new-agent
model: anthropic:claude-sonnet-4-5
prompt: |
  You are a helpful assistant.
---
`;

  // Handle editor content change - auto-select new agent if none selected
  const handleEditorChange = useCallback((content: string) => {
    // If no agent selected and user starts typing, auto-select "New Agent"
    if (!selectedAgentId && content) {
      setSelectedAgentId(NEW_AGENT_VALUE);
      setIsNewAgent(true);
      setOriginalContent('');
      setHasDraftChanges(true);
    }
    setAgentContent(content);
  }, [selectedAgentId]);

  // Handle agent selection
  const handleAgentSelect = useCallback((agentId: string) => {
    setSelectedAgentId(agentId);
    setOutput(null);
    setUserPrompt('');
    setInputVariables({});
    setPromptConfig({ mode: 'append' });
    setMessages([]);
    setLogs('');
    setTraceEvents([]);
    setStatus('idle');
    setRunError(null);
    setTrajectoryId(null);

    // Handle new agent
    if (agentId === NEW_AGENT_VALUE) {
      setIsNewAgent(true);
      setAgentContent(NEW_AGENT_TEMPLATE);
      setOriginalContent('');
      setHasDraftChanges(true);
    } else {
      setIsNewAgent(false);
    }
  }, []);

  // Extract agent name from content
  const extractAgentName = (content: string): string => {
    const match = content.match(/^---[\s\S]*?name:\s*([^\n]+)/m);
    return match ? match[1].trim() : 'untitled-agent';
  };

  // Handle publish (create new or publish existing)
  const handlePublish = useCallback(async () => {
    if (!agentContent) return;

    try {
      if (isNewAgent) {
        // Create new agent
        const agentName = extractAgentName(agentContent);
        const result = await contentApi.createItem({
          name: agentName,
          content_type: 'agent',
          folder_path: '/agents',
          content: agentContent,  // createItem handles base64 encoding
          publish: true,
        });

        // Update state to reflect the new agent
        setSelectedAgentId(result.id);
        setIsNewAgent(false);
        setOriginalContent(agentContent);
        setHasDraftChanges(false);

        // Refresh agents list
        const items = await contentApi.listItems({ contentType: 'agent' });
        setAgents(items);

        toast({
          title: 'Created',
          description: `Agent "${agentName}" created successfully`,
        });
      } else if (selectedAgentId && selectedAgentId !== NEW_AGENT_VALUE) {
        // Publish existing agent
        await contentApi.publishVersion(selectedAgentId);
        setOriginalContent(agentContent);
        setHasDraftChanges(false);
        toast({
          title: 'Published',
          description: 'Agent published successfully',
        });
      }
    } catch (error) {
      console.error('Failed to publish:', error);
      toast({
        title: 'Error',
        description: isNewAgent ? 'Failed to create agent' : 'Failed to publish agent',
        variant: 'destructive',
      });
    }
  }, [selectedAgentId, agentContent, isNewAgent, toast]);

  // Handle run (one-shot mode)
  const handleRun = useCallback(async () => {
    if (!selectedAgentId || isRunning) return;

    setIsRunning(true);
    setStatus('running');
    setOutput(null);
    setRunError(null);
    setLogs('');
    setTraceEvents([]);

    try {
      // Apply prompt mode to determine how content/variables are sent
      let finalContent = agentContent;
      let finalVariables = { ...inputVariables };
      let finalUserPrompt = userPrompt;

      if (promptConfig.mode === 'override' && userPrompt) {
        // Replace the prompt field in frontmatter with user's prompt
        // Parse the frontmatter and body, replace prompt
        const frontmatterMatch = agentContent.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
        if (frontmatterMatch) {
          const frontmatter = frontmatterMatch[1];
          // Replace or add the prompt field
          let newFrontmatter = frontmatter;
          if (/^prompt:/m.test(frontmatter)) {
            // Replace existing prompt (handles multi-line with |)
            newFrontmatter = frontmatter.replace(/^prompt:.*(?:\n(?:  |\t).*)*$/m, `prompt: |\n  ${userPrompt.replace(/\n/g, '\n  ')}`);
          } else {
            // Add prompt field
            newFrontmatter = `${frontmatter}\nprompt: |\n  ${userPrompt.replace(/\n/g, '\n  ')}`;
          }
          finalContent = `---\n${newFrontmatter}\n---\n`;
          // Clear user prompt since it's now in the agent body
          finalUserPrompt = '';
        }
      } else if (promptConfig.mode === 'variable' && promptConfig.targetVariable && userPrompt) {
        // Add user prompt as an input variable
        finalVariables[promptConfig.targetVariable] = userPrompt;
        // Clear user prompt since it's now a variable
        finalUserPrompt = '';
      }
      // 'append' mode: use user_prompt as-is (default behavior)

      const result = await playgroundApi.run({
        agent_id: selectedAgentId,
        input_variables: finalVariables,
        user_prompt: finalUserPrompt,
        agent_content: finalContent,
      });

      setTrajectoryId(result.trajectory_id);
      // Polling will handle the rest
    } catch (error: any) {
      setIsRunning(false);
      setStatus('failed');
      setRunError(error.message || 'Failed to run agent');
    }
  }, [selectedAgentId, isRunning, userPrompt, inputVariables, agentContent, promptConfig]);

  // Handle stop
  const handleStop = useCallback(async () => {
    if (trajectoryId) {
      try {
        await playgroundApi.stop(trajectoryId);
      } catch (error) {
        console.error('Failed to stop:', error);
      }
    }
    if (chatControllerRef.current) {
      chatControllerRef.current.abort();
    }
    setIsRunning(false);
    setStatus('idle');
  }, [trajectoryId]);

  // Handle chat message (interactive mode)
  const handleSendMessage = useCallback(async (content: string) => {
    if (!selectedAgentId || isRunning) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsRunning(true);

    const assistantMessageId = `assistant-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
      },
    ]);

    let accumulatedContent = '';

    chatControllerRef.current = playgroundApi.chat(
      {
        agent_id: selectedAgentId,
        trajectory_id: trajectoryId || undefined,
        messages: [...messages, userMessage].map((m) => ({
          role: m.role,
          content: m.content,
        })),
        input_variables: inputVariables,
        agent_content: agentContent,  // Always send content for execution
      },
      (data) => {
        if (data.type === 'text') {
          accumulatedContent += data.content;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessageId
                ? { ...m, content: accumulatedContent }
                : m
            )
          );
        } else if (data.type === 'trajectory_id') {
          setTrajectoryId(data.trajectory_id);
        }
      },
      (error) => {
        setRunError(error.message);
        setIsRunning(false);
      },
      () => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId ? { ...m, isStreaming: false } : m
          )
        );
        setIsRunning(false);
      }
    );
  }, [selectedAgentId, isRunning, trajectoryId, messages, inputVariables, hasDraftChanges, agentContent]);

  const handleStopGeneration = useCallback(() => {
    if (chatControllerRef.current) {
      chatControllerRef.current.abort();
    }
    setIsRunning(false);
  }, []);

  const handleClearMessages = useCallback(() => {
    setMessages([]);
    setTrajectoryId(null);
  }, []);

  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-background shrink-0">
        <div className="flex items-center gap-4">
          {/* Agent Selector */}
          <AgentSelector
            value={selectedAgentId}
            onChange={handleAgentSelect}
            agents={agents}
            disabled={isRunning}
          />

          {/* Draft indicator */}
          {hasDraftChanges && (
            <Badge variant="secondary" className="text-xs">
              Draft
            </Badge>
          )}

          {/* Save status */}
          {isSaving && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving...
            </span>
          )}
          {lastSavedAt && !isSaving && (
            <span className="text-xs text-muted-foreground">
              Saved at {lastSavedAt.toLocaleTimeString()}
            </span>
          )}

          {/* Mode Toggle */}
          <Tabs value={mode} onValueChange={(v) => setMode(v as 'oneshot' | 'interactive')}>
            <TabsList className="h-8">
              <TabsTrigger value="oneshot" className="text-xs px-3">One-shot</TabsTrigger>
              <TabsTrigger value="interactive" className="text-xs px-3">Interactive</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex items-center gap-2">
          {/* Run/Stop Button */}
          {isRunning ? (
            <Button variant="destructive" size="sm" onClick={handleStop}>
              <Square className="h-4 w-4 mr-1" />
              Stop
            </Button>
          ) : (
            <Button size="sm" onClick={handleRun} disabled={!selectedAgentId}>
              <Play className="h-4 w-4 mr-1" />
              Run
            </Button>
          )}

          {/* Publish/Create Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handlePublish}
            disabled={!hasDraftChanges && !isNewAgent}
          >
            <Upload className="h-4 w-4 mr-1" />
            {isNewAgent ? 'Create' : 'Publish'}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Editor */}
        <div className="w-1/2 flex flex-col border-r">
          <div className="flex items-center h-10 px-4 border-b shrink-0">
            <Tabs value={leftPanelTab} onValueChange={(v) => setLeftPanelTab(v as 'code' | 'logs' | 'trace')}>
              <TabsList className="h-7">
                <TabsTrigger value="code" className="text-xs px-3">Code</TabsTrigger>
                <TabsTrigger value="logs" className="text-xs px-3">Logs</TabsTrigger>
                <TabsTrigger value="trace" className="text-xs px-3">Trace</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex-1 overflow-hidden">
            {leftPanelTab === 'code' && (
              <div className="h-full p-2">
                <PlaygroundEditor
                  content={agentContent}
                  onChange={handleEditorChange}
                  validationErrors={validationErrors}
                />
              </div>
            )}
            {(leftPanelTab === 'logs' || leftPanelTab === 'trace') && (
              <PlaygroundObserver
                logs={logs}
                traceEvents={traceEvents}
                trajectoryId={trajectoryId}
                activeTab={leftPanelTab}
              />
            )}
          </div>
        </div>

        {/* Right Panel - Input/Output */}
        <div className="w-1/2 flex flex-col">
          <div className="flex items-center h-10 px-4 border-b shrink-0">
            <span className="text-xs font-medium">
              {mode === 'oneshot' ? 'Run Agent' : 'Chat with Agent'}
            </span>
          </div>
          <div className="flex-1 flex flex-col p-4 gap-4 overflow-hidden">
            {mode === 'oneshot' ? (
              <>
                <PlaygroundInput
                  agentContent={agentContent}
                  inputVariables={inputVariables}
                  onVariablesChange={setInputVariables}
                  userPrompt={userPrompt}
                  onUserPromptChange={setUserPrompt}
                  promptConfig={promptConfig}
                  onPromptConfigChange={setPromptConfig}
                  disabled={isRunning}
                />
                <PlaygroundOutput
                  output={output}
                  error={runError}
                  status={status}
                />
              </>
            ) : (
              <>
                {/* Variable inputs for interactive mode (without user prompt) */}
                <PlaygroundInput
                  agentContent={agentContent}
                  inputVariables={inputVariables}
                  onVariablesChange={setInputVariables}
                  promptConfig={promptConfig}
                  onPromptConfigChange={setPromptConfig}
                  disabled={isRunning}
                  showUserPrompt={false}
                />
                <PlaygroundChat
                  messages={messages}
                  isLoading={isRunning}
                  onSendMessage={handleSendMessage}
                  onStopGeneration={handleStopGeneration}
                  onClearMessages={handleClearMessages}
                  disabled={!selectedAgentId}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlaygroundPage;
